/**
 * Per-user musync session for whoever is signed in to Feishin.
 *
 * There is no shared deployment token any more: each Feishin user becomes
 * their own musync user (role, profile, wants, playlists) by exchanging the
 * Navidrome credential Feishin already holds via `POST /auth/navidrome`.
 * musync checks it against Navidrome and returns a session token, which is
 * cached in localStorage per `${serverId}:${username}` so a reload does not
 * cost another round trip to Navidrome.
 *
 * The cache entry is dropped whenever the Feishin login it belongs to changes
 * (other server, other user, new credential, logout), together with every
 * cached `['musync', ...]` query, so one user's playlists or downloads never
 * leak into the next user's screens. The pure rules live in
 * `musync-session-core.ts`.
 */
import axios, { isAxiosError } from 'axios';

import {
    CachedMusyncSession,
    capabilitiesFromMe,
    createMusyncSessionManager,
    MusyncSessionError,
    MusyncSessionIdentity,
    musyncSessionIdentity,
    MusyncSessionRequest,
    MusyncSessionServer,
    MusyncSessionStorage,
    responseDetail,
    staleMusyncSessionKey,
} from '/@/renderer/api/musync/musync-session-core';
import { queryClient } from '/@/renderer/lib/react-query';
import { useAuthStore } from '/@/renderer/store/auth.store';

interface NavidromeExchangeResponse {
    expires_at?: null | string;
    me?: unknown;
    token?: string;
}

export { MusyncSessionError };

/** Query key of the current login's capabilities (see `useMusyncPermissions`). */
export const musyncSessionQueryKey = (cacheKey: null | string) =>
    ['musync', 'session', cacheKey || ''] as const;

const memoryStorage = new Map<string, string>();
const fallbackStorage: MusyncSessionStorage = {
    getItem: (key) => memoryStorage.get(key) ?? null,
    removeItem: (key) => {
        memoryStorage.delete(key);
    },
    setItem: (key, value) => {
        memoryStorage.set(key, value);
    },
};

const sessionStorage = (): MusyncSessionStorage => {
    try {
        if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
    } catch {
        // Storage access can throw in locked-down browsers; keep it in memory.
    }
    return fallbackStorage;
};

const toSessionServer = (
    server: null | undefined | { credential?: string; id: string; type: string; username: string },
): MusyncSessionServer | null =>
    server
        ? {
              credential: server.credential,
              id: server.id,
              type: server.type,
              username: server.username,
          }
        : null;

/** The identity of the current Feishin login, or null if it cannot use musync. */
export const currentMusyncIdentity = (): MusyncSessionIdentity | null =>
    musyncSessionIdentity(toSessionServer(useAuthStore.getState().currentServer));

/** musync's base URL, as injected by `settings.js` (no trailing slash). */
export const readMusyncBaseUrl = (): string => {
    const fromWindow = typeof window !== 'undefined' ? window.MUSYNC_URL : undefined;
    return (fromWindow || '').replace(/\/+$/, '');
};

const exchange = async (identity: MusyncSessionIdentity): Promise<CachedMusyncSession> => {
    try {
        const { data } = await axios.post<NavidromeExchangeResponse>(
            `${readMusyncBaseUrl()}/api/v1/auth/navidrome`,
            { credential: identity.credential },
            { timeout: 15_000 },
        );
        if (!data || typeof data.token !== 'string' || !data.token) {
            throw new MusyncSessionError('musync returned no session token');
        }
        return {
            capabilities: capabilitiesFromMe(data.me),
            expires_at: data.expires_at ?? null,
            token: data.token,
        };
    } catch (error) {
        if (error instanceof MusyncSessionError) throw error;
        const response = isAxiosError(error) ? error.response : undefined;
        throw new MusyncSessionError(
            'musync sign-in failed',
            response?.status ?? null,
            error,
            responseDetail(response?.data),
        );
    }
};

/**
 * The one session manager of this page. A token is only cached and only
 * handed out while the login it was obtained for is still the current one.
 */
export const musyncSessions = createMusyncSessionManager({
    currentIdentity: currentMusyncIdentity,
    exchange,
    onStored: (cacheKey, session) => {
        // The role may have changed since the last exchange.
        queryClient.setQueryData(musyncSessionQueryKey(cacheKey), session.capabilities ?? null);
    },
    storage: sessionStorage,
});

/**
 * Session for the current login (or for `cacheKey`, failing if that is no
 * longer the current login): the cached one unless `force`, else a fresh
 * exchange. Throws `MusyncSessionError` when no session can be had.
 */
export const getMusyncSession = (
    request: MusyncSessionRequest = {},
): Promise<CachedMusyncSession> => musyncSessions.getSession(request);

let subscribed = false;

/** Drop the previous login's session and musync data whenever the Feishin login changes. */
const watchFeishinLogin = () => {
    if (subscribed) return;
    subscribed = true;
    useAuthStore.subscribe((state, previous) => {
        const staleKey = staleMusyncSessionKey(
            toSessionServer(previous.currentServer),
            toSessionServer(state.currentServer),
        );
        if (!staleKey) return;
        musyncSessions.forget(staleKey);
        queryClient.removeQueries({ queryKey: ['musync'] });
    });
};

watchFeishinLogin();
